import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { getTenantId } from '@/lib/getTenant';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';
export type NotificationCategory = 'lead' | 'payment' | 'signature' | 'addon' | 'delivery' | 'appointment' | 'system';

export interface Notification {
  id: string;
  tenant_id: string;
  user_id: string;
  type: NotificationType;
  category: NotificationCategory | null;
  title: string;
  message: string;
  is_read: boolean;
  entity_type: string | null;
  entity_id: string | null;
  created_at: string;
}

export type NotificationFilter = 'all' | NotificationCategory;

export function useNotifications(filter: NotificationFilter = 'all') {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const unreadByCategory = useCallback((cat: NotificationCategory) =>
    notifications.filter((n) => !n.is_read && n.category === cat).length,
  [notifications]);

  const fetchNotifications = useCallback(async () => {
    const tenantId = await getTenantId();
    if (!tenantId) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    let query = supabase
      .from('notifications')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (filter !== 'all') {
      query = query.eq('category', filter);
    }

    const { data, error } = await query.limit(50);

    if (!error && data) {
      setNotifications(data as Notification[]);
    }
    setLoading(false);
  }, [filter]);

  const markAsRead = useCallback(async (id: string) => {
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', id);

    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    let query = supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);

    if (filter !== 'all') {
      query = query.eq('category', filter);
    }

    await query;

    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }, [filter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Realtime subscription
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      channel = supabase
        .channel('notifications-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            setNotifications((prev) => {
              const newNotif = payload.new as Notification;
              // Filter out if category doesn't match current filter
              if (filter !== 'all' && newNotif.category !== filter) return prev;
              const already = prev.find((n) => n.id === newNotif.id);
              if (already) return prev;
              return [newNotif, ...prev].slice(0, 50);
            });
          }
        )
        .subscribe();
    })();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [filter]);

  return { notifications, unreadCount, unreadByCategory, loading, markAsRead, markAllAsRead, refetch: fetchNotifications };
}